import {
  Box,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Select,
  VStack,
  useColorModeValue,
  SimpleGrid,
} from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { consoleId } from "../../../data/consoles";

const ItemSection = ({ itemData, onUpdate }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const labelBgColor = useColorModeValue("gray.100", "gray.700");

  const [item, setItem] = useState({
    consoleType: "",
    consoleName: "",
    consoleModel: "",
    consoleColour: "",
    consoleDrive: "",
    consoleHdd: "",
    consoleSerial: "",
    accessor: "",
    other: "",
  });

  const consoleTypes = [
    { id: consoleId.PS5_Disc, name: "PS5" },
    { id: consoleId.XboxSeriesX, name: "Xbox Series X/S" },
    { id: consoleId.NintendoSwitch, name: "Nintendo Switch" },
    { id: consoleId.PS4, name: "PS4" },
    { id: consoleId.XboxOne, name: "Xbox One" },
    { id: consoleId.WiiU, name: "Wii U" },
    { id: consoleId.PS3, name: "PS3" },
    { id: consoleId.Xbox360, name: "Xbox 360" },
    { id: consoleId.Wii, name: "Wii" },
    { id: consoleId.PS2, name: "PS2" },
    { id: consoleId.Xbox, name: "Xbox" },
    { id: consoleId.GameCube, name: "GameCube" },
    { id: consoleId.Other, name: "Other" },
  ];

  useEffect(() => {
    if (itemData) {
      setItem({
        consoleType: itemData.consoleType || "",
        consoleName: itemData.consoleName || "",
        consoleModel: itemData.consoleModel || "",
        consoleColour: itemData.consoleColour || "",
        consoleDrive: itemData.consoleDrive || "",
        consoleHdd: itemData.consoleHdd || "",
        consoleSerial: itemData.consoleSerial || "",
        accessor: itemData.accessor || "",
        other: itemData.other || "",
      });
    }
  }, [itemData]);

  const handleChange = (field, value) => {
    const updated = { ...item, [field]: value };
    setItem(updated);

    // Update console name when type changes
    if (field === "consoleType") {
      const selectedConsole = consoleTypes.find((c) => c.id === value);
      if (selectedConsole && value !== consoleId.Other) {
        updated.consoleName = selectedConsole.name;
        updated.other = "";
      } else if (value === consoleId.Other) {
        updated.consoleName = "";
      }
    }

    // If other is being used, update consoleName
    if (field === "other" && item.consoleType === consoleId.Other) {
      updated.consoleName = value;
    }

    // Map to uppercase field names for parent
    onUpdate({
      CONSOLE_TYPE: updated.consoleType,
      CONSOLE_NAME: updated.consoleName,
      CONSOLE_MODEL: updated.consoleModel,
      CONSOLE_COLOUR: updated.consoleColour,
      CONSOLE_DRIVE: updated.consoleDrive,
      CONSOLE_HDD: updated.consoleHdd,
      CONSOLE_SERIAL: updated.consoleSerial,
      ACCESSOR: updated.accessor,
    });
  };

  return (
    <Box bg={bgColor} p={3} borderRadius='md' border='1px' borderColor={borderColor} height='100%'>
      <Heading size='sm' bg={labelBgColor} p={2} borderRadius='md' mb={3}>
        Item
      </Heading>

      <VStack spacing={2} alignItems='stretch'>
        <FormControl isRequired>
          <FormLabel fontSize='xs'>Console Type</FormLabel>
          <Select
            size='sm'
            value={item.consoleType}
            onChange={(e) => handleChange("consoleType", e.target.value)}
            placeholder='Select console type'
          >
            {consoleTypes.map((console) => (
              <option key={console.id} value={console.id}>
                {console.name}
              </option>
            ))}
          </Select>
        </FormControl>

        {item.consoleType === consoleId.Other && (
          <FormControl isRequired>
            <FormLabel fontSize='xs'>Other Console Name</FormLabel>
            <Input
              size='sm'
              value={item.other}
              onChange={(e) => handleChange("other", e.target.value)}
              placeholder='Enter console name'
            />
          </FormControl>
        )}

        <SimpleGrid columns={2} spacing={2}>
          <FormControl>
            <FormLabel fontSize='xs'>Model</FormLabel>
            <Input
              size='sm'
              value={item.consoleModel}
              onChange={(e) => handleChange("consoleModel", e.target.value)}
              placeholder='Model'
            />
          </FormControl>

          <FormControl>
            <FormLabel fontSize='xs'>Colour</FormLabel>
            <Input
              size='sm'
              value={item.consoleColour}
              onChange={(e) => handleChange("consoleColour", e.target.value)}
              placeholder='Colour'
            />
          </FormControl>
        </SimpleGrid>

        <SimpleGrid columns={2} spacing={2}>
          <FormControl>
            <FormLabel fontSize='xs'>Drive Type</FormLabel>
            <Select
              size='sm'
              value={item.consoleDrive}
              onChange={(e) => handleChange("consoleDrive", e.target.value)}
              placeholder='Select drive'
            >
              <option value='P'>Physical</option>
              <option value='D'>Digital</option>
              <option value='T'>Top Loading</option>
              <option value='Y'>Yes</option>
              <option value='N'>No</option>
            </Select>
          </FormControl>

          <FormControl>
            <FormLabel fontSize='xs'>HDD Size</FormLabel>
            <Input
              size='sm'
              value={item.consoleHdd}
              onChange={(e) => handleChange("consoleHdd", e.target.value)}
              placeholder='e.g., 500GB'
            />
          </FormControl>
        </SimpleGrid>

        <FormControl>
          <FormLabel fontSize='xs'>Serial Number</FormLabel>
          <Input
            size='sm'
            value={item.consoleSerial}
            onChange={(e) => handleChange("consoleSerial", e.target.value.toUpperCase())}
            placeholder='Serial Number'
          />
        </FormControl>

        <FormControl>
          <FormLabel fontSize='xs'>Accessories / Cables</FormLabel>
          <Input
            size='sm'
            value={item.accessor}
            onChange={(e) => handleChange("accessor", e.target.value)}
            placeholder='e.g., Power cable, HDMI, Controller'
          />
        </FormControl>
      </VStack>
    </Box>
  );
};

export default ItemSection;
